import React from 'react';

import '../../index.less';

import IbTypography from '../../../common/IbTypography';
import IbScrollSpy from '../../../common/IbScrollSpy';
import {
  BLOCK_CLASS_NAME,
  BLOCK_ITEM_CLASS_NAME,
  BLOCK_TITLE_CLASS_NAME,
  CHECKBOX_CLASS_NAME,
  CONTENT_CLASS_NAME,
  ELMA365_TOOL_MAIN_SETTINGS_CLASS_NAME,
  MAIN_CLASS_NAME,
} from '../../const';
import { ELMA365ToolModel } from '../../../../../gamma-api';
import { getTranslateFunc } from '../../../../utils/stringUtil';
import IbInput from '../../../common/IbInput';
import { CodeEditorSize } from '../../../common/CodeEditorExpandable/types';
import IbCheckbox from '../../../common/IbCheckbox';
import { UseValidationForm } from '../../../../utils/validationUtil';
import { ELMA365ToolForm } from '../../../../pages/toolkits/elma365/ToolPage';
import PromptEditorExpandable from '../../../common/PromptEditorExpandable';
import { PromptLanguage } from '../../../common/PromptEditor/const';

export interface IELMA365ToolMainProps {
  tool: ELMA365ToolModel;
  validationForm: UseValidationForm<ELMA365ToolForm>;
  onChange: (tool: ELMA365ToolModel) => void;
}

const ELMA365ToolMain: React.FC<IELMA365ToolMainProps> = ({ tool, validationForm, onChange }) => {
  const translate = getTranslateFunc('Toolkits: ELMA365: Tool: Main:');

  const onToolNameChange = (value: string) => {
    onChange({ ...tool, attributes: { ...tool.attributes, name: { ...tool.attributes.name, value } } });
    validationForm.onFieldChange('name', value);
  };

  const onToolNameBlur = () => {
    validationForm.onFieldBlur('name');
  };

  const onToolDescriptionChange = (value: string) => {
    onChange({ ...tool, attributes: { ...tool.attributes, description: { ...tool.attributes.description, value } } });
  };

  const onReadOnlyChange = (value: boolean) => {
    onChange({
      ...tool,
      annotations: { ...tool.annotations, readOnlyHint: { ...tool.annotations.readOnlyHint, value } },
    });
  };

  const onIdempotentChange = (value: boolean) => {
    onChange({
      ...tool,
      annotations: { ...tool.annotations, idempotentHint: { ...tool.annotations.idempotentHint, value } },
    });
  };

  const onDestructiveChange = (value: boolean) => {
    onChange({
      ...tool,
      annotations: { ...tool.annotations, destructiveHint: { ...tool.annotations.destructiveHint, value } },
    });
  };

  const renderGeneralBlock = () => {
    return (
      <div className={BLOCK_CLASS_NAME}>
        <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
          <h4>{translate('General: Title')}</h4>
        </IbTypography>
        <div className={BLOCK_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {translate('General: Tool name: Title')}
          </IbTypography.Paragraph>
          <IbInput
            status={validationForm.getShowError(validationForm.validationState.name) ? 'error' : 'default'}
            value={tool.attributes.name.value}
            onBlur={onToolNameBlur}
            onChange={onToolNameChange}
          />
          {validationForm.getShowError(validationForm.validationState.name) && (
            <IbTypography.Paragraph error type="descriptor">
              {validationForm.getErrorMessage(
                validationForm.validationState.name,
                translate('General: Tool name: Error')
              )}
            </IbTypography.Paragraph>
          )}
        </div>
        <div className={BLOCK_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {translate('General: Tool description: Title')}
          </IbTypography.Paragraph>
          <PromptEditorExpandable
            promptLanguage={PromptLanguage.PLAIN_TEXT}
            size={CodeEditorSize.XL}
            value={tool.attributes.description.value}
            onChange={onToolDescriptionChange}
          />
          <div className={CHECKBOX_CLASS_NAME}>
            <IbCheckbox value={!!tool.annotations.readOnlyHint.value} onChange={onReadOnlyChange} />
            <IbTypography.Paragraph type="secondary">
              {translate('General: Tool readonly: Title')}
            </IbTypography.Paragraph>
          </div>
          <div className={CHECKBOX_CLASS_NAME}>
            <IbCheckbox value={!!tool.annotations.idempotentHint.value} onChange={onIdempotentChange} />
            <IbTypography.Paragraph type="secondary">
              {translate('General: Tool idempotent: Title')}
            </IbTypography.Paragraph>
          </div>
          <div className={CHECKBOX_CLASS_NAME}>
            <IbCheckbox value={!!tool.annotations.destructiveHint.value} onChange={onDestructiveChange} />
            <IbTypography.Paragraph type="secondary">
              {translate('General: Tool destructive: Title')}
            </IbTypography.Paragraph>
          </div>
        </div>
      </div>
    );
  };

  const sections = [
    {
      id: 'general',
      title: translate('General: Title'),
      content: renderGeneralBlock(),
    },
  ];

  return (
    <div className={MAIN_CLASS_NAME}>
      <div className={`${CONTENT_CLASS_NAME} ${ELMA365_TOOL_MAIN_SETTINGS_CLASS_NAME}`}>
        <IbScrollSpy displayNav sections={sections} />
      </div>
    </div>
  );
};

export default ELMA365ToolMain;
